// @greenlight-spec: 32-knowledge.md#assets
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Copy, ImagePlus, Pencil, Trash2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Eyebrow } from '@/components/greenlight/Eyebrow';
import { api } from '@/lib/api';
import { useEntryScopedAction } from '@/components/knowledge/useEntryScopedAction';
import { errorBodyMessage } from '@/lib/apiErrors';
import { copyTextToClipboard } from '@/lib/clipboard';
import type { KnowledgeAsset } from '@greenlight/contract';

const ROLES = [
  'logo-primary',
  'logo-mark',
  'logo-wordmark',
  'icon',
  'favicon',
  'illustration',
  'diagram',
  'other',
] as const;

const THEMES = ['any', 'light', 'dark'] as const;

// The mutations' resolved shapes, which ts-rest gives as a status union.
type CreateResult = Awaited<
  ReturnType<ReturnType<typeof api.knowledge.createAsset.useMutation>['mutateAsync']>
>;
type UpdateResult = Awaited<
  ReturnType<ReturnType<typeof api.knowledge.updateAsset.useMutation>['mutateAsync']>
>;
type DeleteResult = Awaited<
  ReturnType<ReturnType<typeof api.knowledge.deleteAsset.useMutation>['mutateAsync']>
>;

const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/webp'];
const ACCEPT = ACCEPTED_TYPES.join(',');

const formatBytes = (bytes: number): string =>
  bytes < 1024 ? `${bytes} B` : `${Math.round(bytes / 1024)} KB`;

/**
 * A thumbnail on both backgrounds. A mark drawn for a dark header disappears on
 * a light one, and the whole point of this surface is catching that here rather
 * than after it ships inside an app.
 */
function AssetThumbnail({ asset }: { asset: KnowledgeAsset }): React.JSX.Element {
  // The preview URL is signed for 15 minutes. A drawer left open longer outlives
  // it, and the browser would then paint a broken-image frame — the one thing
  // docs/32 rules out for a missing preview. Treat a load failure as no preview.
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [asset.previewUrl]);

  if (!asset.previewUrl || failed) {
    return (
      <div className="flex h-16 items-center justify-center rounded-[var(--radius)] border border-border bg-muted text-xs text-muted-foreground">
        No preview
      </div>
    );
  }
  const showLight = asset.theme !== 'dark';
  const showDark = asset.theme !== 'light';
  return (
    <div className="flex gap-2">
      {showLight ? (
        <div className="flex h-16 flex-1 items-center justify-center rounded-[var(--radius)] border border-border bg-white p-2">
          <img
            src={asset.previewUrl}
            alt={asset.description}
            onError={() => setFailed(true)}
            className="max-h-full max-w-full object-contain"
          />
        </div>
      ) : null}
      {showDark ? (
        <div className="flex h-16 flex-1 items-center justify-center rounded-[var(--radius)] border border-border bg-neutral-900 p-2">
          <img
            src={asset.previewUrl}
            alt=""
            aria-hidden
            onError={() => setFailed(true)}
            className="max-h-full max-w-full object-contain"
          />
        </div>
      ) : null}
    </div>
  );
}

export interface KnowledgeAssetsProps {
  entryId: string;
  /** Assets follow the entry: no edit rights on the body means none here either. */
  canEdit: boolean;
}

/**
 * The Assets section of the entry editor (docs/28 §`/knowledge` → Assets). This
 * is where IT puts the company logo so builder agents use the real mark instead
 * of generating one.
 */
/** Derive a server-valid slug from a filename: leading letter, no trailing hyphen. */
function slugFromFilename(name: string): string {
  return name
    .replace(/\.[^.]+$/, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^[^a-z]+/, '')
    .replace(/-+$/, '');
}

export function KnowledgeAssets({ entryId, canEdit }: KnowledgeAssetsProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = api.knowledge.listAssets.useQuery(
    ['knowledge-assets', entryId],
    { params: { id: entryId } },
  );
  // ts-rest resolves a 4xx/5xx into `data` rather than rejecting, so `isError`
  // alone leaves a failed read looking like an empty list — which invites IT to
  // re-upload assets that are already there. Check the status too.
  const loadFailed = isError || (data !== undefined && data.status !== 200);
  // Whether the entry's assets are *known*, not merely empty. The two are
  // indistinguishable in `assets` below, and the replace check depends on
  // telling them apart: an unknown list cannot rule out a slug collision.
  const assetsKnown = !loadFailed && data?.status === 200;
  const assets = !loadFailed && data?.status === 200 ? data.body.items : [];
  const createMutation = api.knowledge.createAsset.useMutation();
  const updateMutation = api.knowledge.updateAsset.useMutation();
  const deleteMutation = api.knowledge.deleteAsset.useMutation();

  const [file, setFile] = React.useState<File | null>(null);
  const [slug, setSlug] = React.useState('');
  const [role, setRole] = React.useState<KnowledgeAsset['role']>('logo-primary');
  const [theme, setTheme] = React.useState<KnowledgeAsset['theme']>('any');
  const [description, setDescription] = React.useState('');
  const [clientError, setClientError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // The drawer can swap entries without unmounting this section, and the form
  // holds no entry id of its own — so without this a file picked for one entry
  // would be attached to the next one. Adjusting state during render rather than
  // in an effect keeps the stale values from ever being shown.
  // One per action, so each keeps its own pending flag and its own view of which
  // entry it belongs to. See useEntryScopedAction for why all three are needed.
  const uploadAction = useEntryScopedAction<CreateResult>(entryId);
  const editAction = useEntryScopedAction<UpdateResult>(entryId);
  const deleteAction = useEntryScopedAction<DeleteResult>(entryId);

  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editDraft, setEditDraft] = React.useState<{
    role: KnowledgeAsset['role'];
    theme: KnowledgeAsset['theme'];
    description: string;
  }>({ role: 'other', theme: 'any', description: '' });

  const [renderedEntryId, setRenderedEntryId] = React.useState(entryId);
  if (renderedEntryId !== entryId) {
    setRenderedEntryId(entryId);
    setFile(null);
    setSlug('');
    setRole('logo-primary');
    setTheme('any');
    setDescription('');
    setClientError(null);
    setEditingId(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // The upload upserts on (entry, slug), which is the documented "replace bytes"
  // action — but a slug that collides by accident would silently overwrite a live
  // logo while the toast said "Attached". Naming it makes the replace deliberate.
  const replacingSlug = assets.some((a) => a.slug === slug);

  // Metadata editing (docs/28 §Assets: "upload, replace bytes, edit metadata,
  // delete"). Role, theme and description are the fields the agent reads, so a
  // wrong one had to be fixed by deleting the file and re-uploading it.

  const startEdit = (asset: KnowledgeAsset): void => {
    setEditingId(asset.id);
    setEditDraft({ role: asset.role, theme: asset.theme, description: asset.description });
  };

  const saveEdit = async (asset: KnowledgeAsset): Promise<void> => {
    await editAction.run({
      perform: () => updateMutation.mutateAsync({ params: { id: asset.id }, body: editDraft }),
      settle: (result, target) => {
        if (result.status === 200) {
          void queryClient.invalidateQueries({ queryKey: ['knowledge-assets', target] });
        }
      },
      report: (result) => {
        if (result.status === 200) {
          toast.success(`Updated ${asset.slug}.`);
          setEditingId(null);
        } else {
          toast.error(errorBodyMessage(result.body, `Could not update ${asset.slug}.`));
        }
      },
      reportError: toast.error,
    });
  };

  const resetForm = (): void => {
    setFile(null);
    setSlug('');
    setDescription('');
    setClientError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onPickFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const picked = event.target.files?.[0] ?? null;
    setClientError(null);
    if (!picked) {
      setFile(null);
      return;
    }
    // Mirror the server's rules so the common mistakes are caught before a 2 MiB
    // round-trip; the server re-checks everything regardless.
    if (picked.size > MAX_BYTES) {
      setClientError(`${picked.name} is ${formatBytes(picked.size)} — the limit is 2 MB.`);
      setFile(null);
      return;
    }
    // `accept` filters the picker but does not bind a drop or a manual override,
    // so the type is checked here too. The server re-checks against magic bytes
    // regardless; this just says which rule fired without a 2 MiB round-trip.
    if (!ACCEPTED_TYPES.includes(picked.type)) {
      setClientError(
        `${picked.name} is ${picked.type || 'an unrecognized type'} — attach an SVG, PNG, JPEG, or WebP.`,
      );
      setFile(null);
      return;
    }
    setFile(picked);
    // KnowledgeAssetSlugSchema wants a leading letter and no trailing hyphen, so
    // a normally-named file like `Acme Logo (final).png` has to be trimmed here
    // rather than bounced by the server.
    if (!slug) setSlug(slugFromFilename(picked.name) || role);
  };

  const onUpload = async (): Promise<void> => {
    if (!file) return;
    setClientError(null);
    const bytes = new Uint8Array(await file.arrayBuffer());
    let binary = '';
    for (const byte of bytes) binary += String.fromCharCode(byte);
    const uploadedSlug = slug;

    await uploadAction.run({
      perform: () =>
        createMutation.mutateAsync({
          params: { id: entryId },
          body: {
            slug,
            role,
            theme,
            filename: file.name,
            contentType: file.type,
            description,
            bytesBase64: btoa(binary),
          },
        }),
      settle: (result, target) => {
        if (result.status === 201) {
          void queryClient.invalidateQueries({ queryKey: ['knowledge-assets', target] });
        }
      },
      report: (result) => {
        if (result.status === 201) {
          toast.success(replacingSlug ? `Replaced ${uploadedSlug}.` : `Attached ${uploadedSlug}.`);
          resetForm();
        } else {
          setClientError(errorBodyMessage(result.body, 'Upload failed.'));
        }
      },
      reportError: setClientError,
    });
  };

  const onDelete = async (asset: KnowledgeAsset): Promise<void> => {
    await deleteAction.run({
      perform: () => deleteMutation.mutateAsync({ params: { id: asset.id } }),
      settle: (result, target) => {
        if (result.status === 204) {
          void queryClient.invalidateQueries({ queryKey: ['knowledge-assets', target] });
        }
      },
      report: (result) => {
        if (result.status === 204) toast.success(`Deleted ${asset.slug}.`);
        else toast.error(errorBodyMessage(result.body, `Could not delete ${asset.slug}.`));
      },
      reportError: toast.error,
    });
  };

  const copyReference = async (asset: KnowledgeAsset): Promise<void> => {
    const copied = await copyTextToClipboard(`![${asset.description}](gl-asset:${asset.slug})`);
    if (copied) toast.success('Reference copied — paste it into the entry body.');
    else toast.error('Could not copy the reference.');
  };

  return (
    <section className="space-y-4">
      <Eyebrow>Assets</Eyebrow>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : null}

      {loadFailed ? (
        <Alert variant="destructive">
          <AlertDescription>Could not load this entry&rsquo;s assets.</AlertDescription>
        </Alert>
      ) : null}

      {!isLoading && !loadFailed && assets.length === 0 ? (
        <p className="rounded-gl-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
          No assets yet. Attach your logo, icon, and favicon here and builder agents will use them
          instead of generating a mark of their own.
        </p>
      ) : null}

      {assets.length > 0 ? (
        <ul className="space-y-3">
          {assets.map((asset) => (
            <li
              key={asset.id}
              className="space-y-3 rounded-gl-lg border border-border bg-card p-3 md:flex md:items-start md:gap-4 md:space-y-0"
            >
              <div className="md:w-48 md:shrink-0">
                <AssetThumbnail asset={asset} />
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-medium text-foreground">
                    {asset.slug}
                  </span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-foreground">
                    {asset.role}
                  </span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-foreground">
                    {asset.theme}
                  </span>
                </div>
                {editingId === asset.id ? (
                  <div className="space-y-2 pt-1">
                    <div className="flex flex-wrap gap-2">
                      <div className="space-y-1">
                        <Label htmlFor={`edit-role-${asset.id}`} className="text-xs">
                          Role
                        </Label>
                        <select
                          id={`edit-role-${asset.id}`}
                          value={editDraft.role}
                          onChange={(e) =>
                            setEditDraft((d) => ({
                              ...d,
                              role: e.target.value as KnowledgeAsset['role'],
                            }))
                          }
                          className="h-11 rounded-gl border border-border bg-background px-2 text-sm md:h-9"
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`edit-theme-${asset.id}`} className="text-xs">
                          Theme
                        </Label>
                        <select
                          id={`edit-theme-${asset.id}`}
                          value={editDraft.theme}
                          onChange={(e) =>
                            setEditDraft((d) => ({
                              ...d,
                              theme: e.target.value as KnowledgeAsset['theme'],
                            }))
                          }
                          className="h-11 rounded-gl border border-border bg-background px-2 text-sm md:h-9"
                        >
                          {THEMES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`edit-description-${asset.id}`} className="text-xs">
                        Description
                      </Label>
                      <Textarea
                        id={`edit-description-${asset.id}`}
                        value={editDraft.description}
                        onChange={(e) =>
                          setEditDraft((d) => ({ ...d, description: e.target.value }))
                        }
                        rows={2}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => void saveEdit(asset)}
                        disabled={!editDraft.description || editAction.pending}
                      >
                        {editAction.pending ? 'Saving…' : 'Save'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{asset.description}</p>
                )}
                <p className="text-xs text-[hsl(var(--text-tertiary))]">
                  {asset.filename} · {formatBytes(asset.byteSize)}
                  {asset.width && asset.height ? ` · ${asset.width}×${asset.height}` : ''}
                </p>
              </div>
              <div className="flex gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-11 w-11 md:h-9 md:w-9"
                      aria-label={`Copy reference for ${asset.slug}`}
                      onClick={() => void copyReference(asset)}
                    >
                      <Copy className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copy Markdown reference</TooltipContent>
                </Tooltip>
                {canEdit ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 md:h-9 md:w-9"
                        aria-label={`Edit ${asset.slug}`}
                        // A save closes whatever editor is open when it lands,
                        // so opening another row's mid-save would discard the
                        // draft typed there.
                        disabled={editAction.pending}
                        onClick={() =>
                          editingId === asset.id ? setEditingId(null) : startEdit(asset)
                        }
                      >
                        <Pencil className="size-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Edit role, theme, and description</TooltipContent>
                  </Tooltip>
                ) : null}
                {canEdit ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 md:h-9 md:w-9"
                        aria-label={`Delete ${asset.slug}`}
                        // A second click while the first delete is in flight
                        // sends another request for a row that is already gone,
                        // producing a success toast followed by a failure one.
                        disabled={deleteAction.pending}
                        onClick={() => void onDelete(asset)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Delete asset and its file</TooltipContent>
                  </Tooltip>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {canEdit ? (
        <div className="space-y-3 rounded-gl-lg border border-border p-3">
          <div className="space-y-1">
            <Label htmlFor="asset-file">Add an asset</Label>
            <Input
              id="asset-file"
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              onChange={onPickFile}
              className="h-11 md:h-9"
            />
            <p className="text-xs text-muted-foreground">SVG, PNG, JPEG, or WebP, up to 2 MB.</p>
          </div>

          {file ? (
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="asset-slug">Slug</Label>
                <Input
                  id="asset-slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="logo-primary"
                  className="h-11 font-mono md:h-9"
                  aria-describedby={replacingSlug ? 'asset-slug-replacing' : undefined}
                />
                {!assetsKnown ? (
                  <p className="text-xs text-[hsl(var(--status-amber-text))]">
                    This entry&rsquo;s assets could not be read, so a slug already in use cannot be
                    detected. Uploading is unavailable until the list loads — otherwise an attach
                    could overwrite a live mark silently.
                  </p>
                ) : null}
                {replacingSlug ? (
                  <p
                    id="asset-slug-replacing"
                    className="text-xs text-[hsl(var(--status-amber-text))]"
                  >
                    Replaces the bytes and metadata of the existing{' '}
                    <span className="font-mono">{slug}</span>.
                  </p>
                ) : null}
              </div>
              <div className="space-y-1">
                <Label htmlFor="asset-role">Role</Label>
                <select
                  id="asset-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as KnowledgeAsset['role'])}
                  className="h-11 w-full rounded-[var(--radius)] border border-border bg-background px-3 text-sm md:h-9"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="asset-theme">Theme</Label>
                <select
                  id="asset-theme"
                  value={theme}
                  onChange={(e) => setTheme(e.target.value as KnowledgeAsset['theme'])}
                  className="h-11 w-full rounded-[var(--radius)] border border-border bg-background px-3 text-sm md:h-9"
                >
                  {THEMES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label htmlFor="asset-description">Description</Label>
                <Textarea
                  id="asset-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  placeholder="Full-colour horizontal lockup. Use on light backgrounds."
                />
                <p className="text-xs text-muted-foreground">
                  Shown as alt text, and read by agents deciding which file to use.
                </p>
              </div>
            </div>
          ) : null}

          {clientError ? (
            <Alert variant="destructive">
              <AlertDescription>{clientError}</AlertDescription>
            </Alert>
          ) : null}

          <Button
            onClick={() => void onUpload()}
            disabled={!file || !slug || !description || !assetsKnown || uploadAction.pending}
            className="h-11 w-full md:h-9 md:w-auto"
          >
            <ImagePlus className="mr-2 size-4" />
            {uploadAction.pending ? 'Uploading…' : replacingSlug ? 'Replace asset' : 'Attach asset'}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
